<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      仕事をキャンセル
    </Header>

    <div class="application-cancel p-3">
      <CustomValidationObserver
        ref="vObserver"
        v-slot="{ handleSubmit }"
        :initialValues="initialValues"
      >
        <ul class="rule-list">
          <li
            v-for="(message, idx) in ruleMessages"
            :key="idx"
            :class="getRuleItemClass(idx)"
            class="text-weight-500 text-red text-size-14 caution"
            data-testid="caution-message"
          >
            <FeatherIcon icon="alert-triangle" size="18" class="caution-icon" />
            {{ message }}
          </li>
        </ul>

        <p class="text-size-14 text-weight-500 mt-4">
          両者のトラブル防止のためにも、キャンセルの詳細を記入してください。
        </p>

        <div class="reason-block mt-4">
          <p class="text-size-14 text-weight-500">アプリからキャンセル</p>
          <ValidationProvider
            v-slot="validationState"
            name="キャンセル理由"
            rules="required"
            vid="selectedReason"
          >
            <FormSelectGroup
              label="キャンセル理由"
              :disabled="isCancelDueToInfection"
              v-model="selectedReason"
              appLabelText="必須"
              :options="options"
              text-field="text"
              value-field="value"
              :validation-state="validationState"
            />
          </ValidationProvider>
        </div>

        <p
          v-if="!isCancelDueToInfection"
          class="text-size-14 text-mild"
          data-testid="contact-form-link"
        >
          ※事業所都合によるキャンセルは<a
            :href="externalLink.contact"
            class="text-blue"
            >問い合わせフォーム</a
          >よりご連絡ください
        </p>

        <div class="reason-block mt-4">
          <ValidationProvider
            v-slot="validationState"
            name="事業所担当者へのメッセージ"
            :rules="{ required: true, max: 2000 }"
            vid="cancelReason"
          >
            <TextAreaGroup
              v-model="cancelReason"
              label="事業所担当者へのメッセージ(キャンセル理由)"
              appLabelText="必須"
              placeholder="37.5度以上の発熱が出てしまい、キャンセルいたします。シフト調整をしている中、現場の方々には、ご迷惑をおかけしてしまい大変申し訳ございません。次回からは発生しないよう、体調管理を行ってまいります。何卒、お願い申し上げます。"
              :validationState="validationState"
              class="mt-1"
            />
          </ValidationProvider>

          <p
            v-if="!isCancelDueToInfection"
            class="text-mild text-size-14 mt-2"
            data-testid="cancel-notice"
          >
            事業所は既にシフトを組んでおり、キャンセルは迷惑をかけるため、キャンセルの詳細およびお詫び文を記入してください。
          </p>
        </div>

        <AppButton
          :block="true"
          :disabled="isExecuting"
          variant="secondary"
          class="d-block mt-4"
          @click="handleSubmit(openConfirmModal)"
        >
          キャンセルする
        </AppButton>
      </CustomValidationObserver>
    </div>
    <BModal
      :visible="isShownConfirmModal"
      title="キャンセルの最終確認"
      header-class="font-weight-bold modal-responsive-title"
      ok-title="キャンセルする"
      cancel-title="やめる"
      cancel-variant="gray-03"
      @ok="cancelApplication"
      @cancel="navigateApplicationDetail"
      @hide="closeConfirmModal"
      centered
    >
      <div class="mb-2">
        以下のお仕事がキャンセルとなります<br />よろしいですか？
      </div>
      <p>事業所名：{{ officeName }}</p>
      <p>日時：{{ formattedDate }} {{ formattedWorkTimeRange }}</p>
      <div
        v-if="!isCancelDueToInfection"
        class="mt-2 modal-responsive-medium text-danger"
      >
        ⚠応募ミスのキャンセルも信用スコアが減点されます
      </div>
    </BModal>
  </div>
</template>

<script lang="ts">
import { applicationService } from "@/app/services/ApplicationService";
import { orderService } from "@/app/services/OrderService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import TextAreaGroup from "@/components/molecules/form/TextAreaGroup.vue";
import Header from "@/components/organisms/common/Header.vue";
import ExternalLink from "@/const/externalLink";
import { authStore } from "@/store/auth";
import { cancelRequestStore } from "@/store/cancel-request";
import { Application } from "@/types/order/Application";
import { CancelType } from "@/types/order/CancelRequest";
import {
  differenceInDays,
  now,
  parseISO,
  startOfDay,
  startOfToday,
  subDays
} from "@/utils/date";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Vue } from "vue-facing-decorator";

@Component({
  components: {
    Header,
    TextAreaGroup,
    AppButton,
    FeatherIcon,
    FormSelectGroup
  }
})
export default class ApplicationCancelReason extends Vue {
  application: Application | null = null;
  isExecuting = false; // 2度押し防止用
  cancelReason = "";
  selectedReason: CancelType | null = null;
  options = [
    { value: "sickness", text: "体調不良" },
    { value: "family_reason", text: "家庭事情" },
    { value: "covid19", text: "コロナウイルス関連" },
    { value: "accident", text: "事故" },
    { value: "disaster", text: "災害等" },
    { value: "traffic", text: "交通機関の停止等" },
    { value: "wrong_application", text: "応募ミス" },
    { value: "others", text: "その他" }
  ];
  externalLink = {
    contact: ExternalLink.CONTACT
  };
  isShownConfirmModal = false;

  get applicationId() {
    return Number(this.$route.params.applicationId);
  }

  get cancelType() {
    return this.$route.query.cancelType;
  }

  get isCancelDueToInfection() {
    return this.cancelType === "infection";
  }

  get initialValues() {
    return {
      selectedReason: this.selectedReason,
      cancelReason: this.cancelReason
    };
  }

  get orderInfo() {
    return this.application?.order;
  }

  get officeInfo() {
    return this.orderInfo?.office;
  }

  get requiredCallToOffice() {
    // データ取得が完了するまでは電話連絡必須扱いにしておく
    if (!this.orderInfo) {
      return true;
    }
    const today = startOfToday();
    const startDate = parseISO(this.application!.appliedStartWorkAt);
    const twoDaysBefore = subDays(startDate, 2);
    // 今日が勤務日の前日以降なら電話連絡必須
    // 0時開始の場合、この条件に合致するのは前日のみ
    return today !== twoDaysBefore && today > twoDaysBefore;
  }

  get isCompletedInput() {
    return this.selectedReason && this.cancelReason;
  }

  get ruleMessages() {
    if (!this.requiredCallToOffice) return ["まだキャンセルは完了していません"];

    const messages = [
      `お仕事開始直前のキャンセルのため、事業所への電話連絡${
        this.isCancelDueToInfection ? "にご協力ください。" : "が必須です。"
      }`
    ];

    if (this.isSuddenCancel) {
      messages.unshift(
        "お仕事開始直前のキャンセルは、やむを得ない理由を除いてお控え下さい。直前のキャンセル率が一定以上の場合、応募した際に事業所からお断りされる可能性があります。"
      );
    }

    return messages;
  }

  get isSuddenCancel() {
    if (!this.application) {
      return false;
    }
    const appliedStartWorkAt = parseISO(this.application.appliedStartWorkAt);
    return now() > subDays(appliedStartWorkAt, 1);
  }

  get officeName() {
    if (!this.officeInfo) {
      return "";
    }
    return this.officeInfo.name;
  }

  get formattedDate(): string {
    if (!this.orderInfo) {
      return "";
    }
    return orderService.getFormattedDateWithSlash(this.orderInfo);
  }

  get formattedWorkTimeRange(): string {
    if (!this.application) {
      return "";
    }
    return applicationService.getFormattedWorkTimeRange(this.application);
  }

  get isCancelledInLast3Days() {
    // データ取得が完了するまでは false を返しておく
    if (!this.application) {
      return false;
    }
    const today = startOfToday();
    const startDate = startOfDay(parseISO(this.application!.order.date));
    return differenceInDays(startDate, today) <= 3;
  }

  async created() {
    try {
      this.$loading.show();
      await this.fetchApplication();
      if (this.isCancelDueToInfection) {
        this.options = [
          { value: "infection", text: "事業所での感染症発生によるキャンセル" }
        ];
        this.selectedReason = "infection";
        this.cancelReason = "事業所で感染症が発生しているため";
      }
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  onClickBack() {
    this.$router.back();
  }

  async cancelApplication() {
    try {
      this.$loading.show();
      this.isExecuting = true;

      await applicationService.createCancelRequest({
        cancelReason: this.cancelReason,
        application: this.applicationId,
        cancelType: this.selectedReason!,
        orderVersion: this.orderInfo!.version,
        orderUpdatedAt: this.orderInfo!.updatedAt
      });
      // 3日以内の勤務日に対するキャンセルの場合、次回応募時に理解度テストを必ず表示するためフラグを立てる
      if (this.isCancelledInLast3Days) {
        authStore.setShouldRequireUnderstandingTest(true);
      }

      // キャンセル申請自体はここで完了しているが、
      // 電話連絡が必要な場合は当該画面に遷移させる
      if (this.requiredCallToOffice) {
        cancelRequestStore.setPhoneCallApplicationId(this.applicationId);
        cancelRequestStore.setCancelType(
          this.isCancelDueToInfection ? "infection" : null
        );
        await this.$router.push({
          name: "ApplicationCancel",
          query: {
            cancelType: this.isCancelDueToInfection
              ? this.selectedReason!
              : undefined
          }
        });
        return;
      }
      await this.$bvModal.msgBoxOk("キャンセルが完了しました。", {
        centered: true
      });
      await this.$router.push({ name: "ApplicationsList" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
      this.isExecuting = false;
    }
  }

  async fetchApplication() {
    this.application = await applicationService.fetchApplication(
      this.applicationId
    );
  }

  getRuleItemClass(idx: number) {
    return idx > 0 ? "mt-2" : "";
  }

  openConfirmModal() {
    this.isShownConfirmModal = true;
  }

  closeConfirmModal() {
    this.isShownConfirmModal = false;
  }

  navigateApplicationDetail() {
    this.$router.push({
      name: "ApplicationsDetail",
      params: { applicationId: this.applicationId }
    });
  }
}
</script>
<style lang="scss" scoped>
.application-cancel {
  .icon-area {
    line-height: 1;
  }
}

.rule-list {
  padding: 16px;
  font-size: 14px;
  background-color: $primary-bg;
  border: 1px solid $yellow;
  border-radius: 4px;
}

.caution {
  position: relative;
  padding-left: 26px;
}

.caution-icon {
  position: absolute;
  top: 3px;
  left: 0;
}
</style>
